<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <section v-if="currentRound" slot="content" class="quotebubble">
        <aside class="activity-side-image">
          <img
            :alt="currentRound.leftImage?.image.description"
            :src="currentRound.leftImage?.image.src"
          />
        </aside>

        <section class="activity-content-container">
          <header class="activity-header-quote">
            <text-display-list>
              <li :class="{ isMobileView: isMobile }" class="quote-bubble-container">
                <div :style="quoteBubbleImage">
                  <blinking-cursor-text
                    :text="currentRound.quote"
                    :text-step="0"
                  ></blinking-cursor-text>
                </div>
              </li>
            </text-display-list>
          </header>

          <div class="activity-subheader">
            <text-display-list class="subheader-words">
              <li>
                <h2>English Word:</h2>
                <blinking-cursor-text
                  :text="currentRound.word"
                  :text-step="1"
                ></blinking-cursor-text>
              </li>

              <li>
                <h2>Meaning:</h2>
                <blinking-cursor-text
                  :text="currentRound.meaning"
                  :text-step="2"
                ></blinking-cursor-text>
              </li>
            </text-display-list>
          </div>

          <div class="activity-paragraph">
            <img
              :alt="currentRound.centerImage?.image.description"
              :src="currentRound.centerImage?.image.src"
              :style="{ maxWidth: isMobile ? '75px' : '150px' }"
            />

            <blinking-cursor-text
              :text="currentRound.paragraph"
              :text-step="3"
            ></blinking-cursor-text>
          </div>
        </section>
      </section>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TimelineLite, TweenMax } from 'gsap';
import { createNamespacedHelpers } from 'vuex';
import quotebubble from 'assets/svg/quotebubble.svg';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';

const application = createNamespacedHelpers('application');

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  data() {
    return {
      activityName: 'QuoteBubble',
      quoteBubbleImage: {
        backgroundImage: `url('data:image/svg+xml;utf8,${quotebubble}')`
      }
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    ...application.mapState({
      isMobile: state => state.device.isMobile
    }),

    isEndOfRound() {
      return this.progress.stepIndex === 4;
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds();
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ leftImage, centerImage }) => {
        this.queueImage(leftImage.image.src);
        this.queueImage(centerImage.image.src);
      });

      return this.downloadImages();
    },

    getTextForStep() {
      const { stepIndex } = this.progress;
      if (stepIndex === 0) return this.currentRound.quote;
      if (stepIndex === 1) return this.currentRound.word;
      if (stepIndex === 2) return this.currentRound.meaning;
      return this.currentRound.paragraph;
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.overwriteProgressText({
        currentText: this.getTextForStep()
      });

      this.$nextTick(() => {
        this.setActivityContainerHeight();
        this.hideStep();
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.handleStepEnd();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleStepEnd() {
      this.sndBell();
      this.finishStep();
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();

      const nextText = this.getTextForStep();
      this.overwriteProgressText({ currentText: nextText });

      if (this.progress.stepIndex === 3) this.fadeInStep();
    },

    fadeInStep() {
      TweenMax.to('.activity-paragraph', 0.5, { opacity: 1 });
    },

    hideStep() {
      TweenMax.set('.activity-paragraph', { opacity: 0 });
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();

      if (this.isEndOfActivity) {
        timeline.delay(1).call(this.completeActivity);
      } else {
        timeline
          .delay(1)
          .to('.activity-stage', 0.5, { opacity: 0 })
          .call(this.setupNewRound)
          .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.quotebubble {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  min-height: 100%;
  height: max-content;

  header {
    background-color: transparent;
  }

  .activity-side-image {
    height: 100%;
    display: flex;
    img {
      height: 98%;
      padding: 0px 20px;
    }
  }

  .activity-content-container {
    width: 100%;
  }

  ul {
    list-style: none;
    -webkit-padding-start: 0;
    margin: 0;
  }

  .activity-header-quote {
    max-width: 705px;
    width: 100%;
    text-align: left;

    ul {
      margin: 0.5em 1em 0 0;
    }
  }

  .quote-bubble-container {
    width: 100%;
    height: 130px;

    div {
      display: flex;
      min-height: 80px;
      min-width: 75%;
      width: max-content;
      height: max-content;
      -moz-background-size: 100% 100%;
      -webkit-background-size: 100% 100%;
      background-size: 100% 100%;
      background-repeat: no-repeat;
      padding: 0.8em 1em;
      p {
        display: block;
        padding: 0.8em 0em 1.2em;
        max-width: 90%;
        min-height: 75px;
      }
    }
  }

  .quote-bubble-container.isMobileView {
    left: -2rem;
    position: relative;
    top: 1.5rem;
  }

  .subheader-words,
  .activity-paragraph {
    display: flex;
    flex-flow: row nowrap;
  }

  .activity-subheader {
    padding-left: 4em;
    margin-top: 2%;

    h2 {
      font-size: 0.9em;
      font-weight: bold;
      margin: 0;
    }

    li {
      margin-right: 2em;
    }
  }

  .activity-paragraph {
    align-items: center;
    padding-right: 2em;

    img {
      height: 100%;
      max-height: 75px;
      width: auto;
      padding: 0 10px 10px 10px;
    }

    p {
      display: block;
      height: 100%;
    }
  }

  p {
    font-size: 20px;
  }
}

.tablet {
  .quotebubble {
    height: 100%;
  }
}
</style>
